'use client';

import { useEffect, useState } from 'react';

/* ---------- SSR-safe localStorage ---------- */
export const safeLocalStorage = {
  getItem(key) {
    if (typeof window === 'undefined') return null;
    try { return window.localStorage.getItem(key); } catch { return null; }
  },
  setItem(key, val) {
    if (typeof window === 'undefined') return;
    try { window.localStorage.setItem(key, val); } catch {}
  },
  removeItem(key) {
    if (typeof window === 'undefined') return;
    try { window.localStorage.removeItem(key); } catch {}
  }
};

/* ---------- token helpers ---------- */
export function getToken() {
  const keys = ['jwt', 'token', 'accessToken'];
  for (const k of keys) {
    const v = safeLocalStorage.getItem(k);
    if (v && typeof v === 'string') return v;
  }
  return null;
}

function b64urlToJson(seg) {
  try {
    // JWT uses base64url ( - _ ), convert to base64 ( + / ) and pad
    const s = seg.replace(/-/g, '+').replace(/_/g, '/')
                 .padEnd(seg.length + (4 - (seg.length % 4 || 4)), '=');
    return JSON.parse(atob(s));
  } catch {
    return {};
  }
}

/* ---------- NEW: validateToken (named export) ---------- */
export function validateToken() {
  try {
    const token = getToken();
    if (!token) return { valid: false, reason: 'missing' };

    const parts = token.split('.');
    if (parts.length !== 3) return { valid: false, reason: 'malformed' };

    const payload = b64urlToJson(parts[1]);
    const now = Date.now();
    const expMs = typeof payload.exp === 'number' ? payload.exp * 1000 : null;
    const expired = expMs ? now >= expMs : false;

    return {
      valid: expMs ? !expired : true,      // treat tokens without exp as valid
      reason: expired ? 'expired' : 'valid',
      expiresAt: expMs,
      expiresWithin5Min: expMs ? (expMs - now) < 5 * 60 * 1000 : false,
      payload,
      token,
    };
  } catch {
    return { valid: false, reason: 'invalid' };
  }
}

/* ---------- NEW: normalization + fetchCurrentUser ---------- */
export function normalizeUser(u) {
  if (!u) return null;
  const uid = u.user_id || u.id || null;

  // Normalize name fields (accept either snake_case or camelCase from server)
  // prefer server-provided snake_case; accept camelCase if present but lower priority
  const rawFirst = u.first_name || u.firstName || '';
  const rawLast = u.last_name || u.lastName || '';

  // If no first name provided, try to derive a friendly name from the email local part
  let derivedFirst = rawFirst;
  if (!derivedFirst && u.email) {
    try {
      const local = String(u.email).split('@')[0] || '';
      derivedFirst = local
        .replace(/[._-]+/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    } catch (e) {
      derivedFirst = '';
    }
  }

  const first_name = derivedFirst || '';
  const last_name = rawLast || '';
  const phone_number = u.phone_number || u.phone || '';
  const full_name = u.fullName || `${first_name} ${last_name}`.trim();

  return {
    ...u,
    id: uid,
    user_id: uid,
    // only expose snake_case name fields to consumers
    first_name,
    last_name,
    phone_number,
    full_name,
    // Make sure KYC status is properly normalized
    kyc_status: u.kyc_status || u.kycStatus || 'incomplete',
    // Ensure risk profile fields are properly normalized
    risk_score: u.risk_score || u.riskScore || null,
    risk_level: u.risk_level || u.riskLevel || null,
  };
}

// Friendly display name helper (consistently uses normalized fields)
export function getDisplayName(u) {
  if (!u) return null;
  if (u.fullName) return u.fullName;
  const first = u.first_name || u.firstName || '';
  const last = u.last_name || u.lastName || '';
  if (first) return `${first}${last ? ' ' + last : ''}`.trim();
  if (u.email) return String(u.email).split('@')[0];
  return null;
}

export async function fetchCurrentUser() {
  const t = validateToken();
  const token = t?.token || null;

  const res = await fetch('/api/auth/me', {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });

  if (res.status === 200) {
    const data = await res.json().catch(() => ({}));
    console.log(data);
    const u = normalizeUser(data?.user || null);
    if (!u) throw new Error('me_no_user');
    safeLocalStorage.setItem('user', JSON.stringify(u)); // cache for other consumers
    return u;
  }
  if (res.status === 401) throw new Error('unauthenticated');
  throw new Error(`me_failed_${res.status}`);
}

/* ---------- Client-only auth hook ---------- */
export function useAuth(_router, opts = { requireAuth: false, redirectTo: '/sign-in' }) {
  const [state, setState] = useState({ isAuthenticated: false, user: null, isLoading: true });

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const tokenObj = validateToken();
        const token = tokenObj.token || getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        // 1) Try /api/auth/me with Bearer header
        let user = null;
        const res = await fetch('/api/auth/me', { method: 'GET', headers, credentials: 'include' });

        if (res.ok) {
          const json = await res.json().catch(() => ({}));

          user = normalizeUser(json?.user ?? null);

          // If the DB returned a user but name fields are missing, prefer the
          // name claims from the JWT payload (tokenObj.payload) if available.
          try {
            const p = tokenObj?.payload || {};
            const hasTokenNames = p && (p.first_name || p.firstName || p.last_name || p.lastName);
            if (user && hasTokenNames) {
              const merged = {
                ...user,
                first_name: user.first_name || p.first_name || p.firstName || '',
                last_name: user.last_name || p.last_name || p.lastName || '',
              };
              user = normalizeUser(merged);
              // try { if (typeof console !== 'undefined') console.debug('useAuth: merged names from token into /me user', { merged }); } catch (e) {}
            }
          } catch (e) {
            // ignore merge errors
          }

        }

        // 2) If we *do* have a token but still no user, try /api/auth/status
        if (!user && token) {
          const res2 = await fetch('/api/auth/status', { method: 'GET', headers, credentials: 'include' });

          if (res2.ok) {
            const j2 = await res2.json().catch(() => ({}));

            if (j2?.authenticated) {
              const rawUser = { id: j2.sub ?? null, email: j2.email ?? null };


              user = normalizeUser(rawUser);

            }
          }
        //   // 3) If still no user, but we have a parsed token payload (from validateToken),
        //   // prefer to populate the user from the token claims which often include
        //   // first_name/last_name (server signs these). This covers cases where /me
        //   // returns null (no DB row found) but the JWT still carries name claims.
        //   if (!user && tokenObj && tokenObj.payload) {
        //     const p = tokenObj.payload || {};
        //     const candidate = {
        //       id: p.user_id || p.sub || p.id || null,
        //       email: p.email || null,
        //       first_name: p.first_name || p.firstName || null,
        //       last_name: p.last_name || p.lastName || null,
        //     };
        //     // Only accept if we at least have an id or email
        //     if (candidate.id || candidate.email) {
        //       // mark this as coming from token payload
        //       user = normalizeUser(candidate);
        //       // small debug to help trace where the data came from
        //       try { if (typeof console !== 'undefined') console.debug('useAuth: populated user from token payload', { fromPayload: p }); } catch (e) {}
        //     }
        //   }
        }

        if (!alive) return;

        const isAuthed = !!(user && (user.id || user.email));
        if (user) {
          safeLocalStorage.setItem('user', JSON.stringify(user)); // keep local cache in sync
        }

        setState({ isAuthenticated: isAuthed, user: user || null, isLoading: false });

        if (opts.requireAuth && !isAuthed && typeof window !== 'undefined') {
          window.location.href = opts.redirectTo || '/sign-in';
        }
      } catch {
        if (!alive) return;
        setState({ isAuthenticated: false, user: null, isLoading: false });
        if (opts.requireAuth && typeof window !== 'undefined') {
          window.location.href = opts.redirectTo || '/sign-in';
        }
      }
    })();


    return () => { alive = false; };
  }, [opts.requireAuth, opts.redirectTo]);

  return state;
}


/* ---------- Add missing helper functions ---------- */

// Simple wrapper for fetchCurrentUser (legacy compatibility)
export async function getUserProfile() {
  try {
    return await fetchCurrentUser();
  } catch (err) {
    console.error('getUserProfile error:', err);
    return null;
  }
}

// Client-safe navigation (used in welcome.jsx or redirects)
export function safeNavigate(router, path) {
  try {
    if (typeof window !== 'undefined' && router?.push) {
      router.push(path);
    } else {
      console.warn('safeNavigate: router not ready');
    }
  } catch (err) {
    console.error('safeNavigate error:', err);
  }
}



/* ---------- default export so `import auth from '@/utils/auth'` works ---------- */
const auth = {
  safeLocalStorage,
  getToken,
  validateToken,
  getUserProfile,
  getRiskProfile,
  normalizeUser,
  getDisplayName,
  fetchCurrentUser,
  useAuth,
  clearAuthData,        // <- added
  storeUserProfile,     // <- added
  requireAuth,          // <- added
  safeNavigate,         // <- added
};
export default auth;

/**
 * Bank-grade header helper: always returns JSON headers and adds Bearer when available.
 */
export function getAuthHeaders(extra = {}) {
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...extra,
  };
  try {
    if (typeof localStorage !== 'undefined') {
      const token = localStorage.getItem('jwt');
      if (token) headers.Authorization = `Bearer ${token}`;
    }
  } catch {}
  return headers;
}

/**
 * Clears client auth artifacts safely.
 */
/**
 * Server-confirmed session check.
 *
 * validateToken() only base64-decodes the payload and checks `exp` — it cannot
 * know the user was deleted, disabled, or had their session revoked. Anything
 * that decides "are you logged in?" must ask the server.
 *
 * Returns { authenticated, user, reason }. On a network failure it returns
 * authenticated:false with transient:true and does NOT clear stored auth —
 * a connectivity blip must never log someone out.
 */
export async function verifySession() {
  const local = validateToken();
  if (!local.valid) return { authenticated: false, user: null, reason: local.reason || 'missing' };

  const base = (process.env.NEXT_PUBLIC_API_URL || '/api').replace(/\/+$/, '');
  try {
    const res = await fetch(`${base}/auth/me`, {
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    if (!res.ok) {
      clearAuthData();
      return { authenticated: false, user: null, reason: 'rejected-' + res.status };
    }
    const data = await res.json().catch(() => null);
    const user = data && data.user;
    if (!user) {
      // Token parses, but the server has no such user — deleted or revoked.
      clearAuthData();
      return { authenticated: false, user: null, reason: 'no-user' };
    }
    return { authenticated: true, user, reason: 'ok' };
  } catch {
    return { authenticated: false, user: null, reason: 'network', transient: true };
  }
}

export function clearAuthData() {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('jwt');
      localStorage.removeItem('user');
      localStorage.removeItem('riskProfile');
      localStorage.removeItem('riskCompleted');
      localStorage.removeItem('kycCompleted');
    }
  } catch {}
}
